import MaterialTable from '@material-table/core';
import { ScheduleOutlined } from '@mui/icons-material';
import {
	Box, Button,
	Card,
	CardContent,
	CardHeader,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	LinearProgress,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography
} from '@mui/material';
import { generateClient, post } from 'aws-amplify/api';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { US } from 'country-region-data';
import { Formik } from 'formik';
import * as _ from 'lodash';
import phone from 'phone';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import {
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { array, lazy, mixed, object, reach, string } from 'yup';
import { ConfirmDialog } from '../../components/confirmDialog/confirmDialog';
import { PageAppBar } from '../../components/pageAppBar';
import QueryBuilder from '../../components/queryBuilder';
import { contactValidation } from '../../components/yupValidations';
import { UserContext } from '../../contexts/UserContext';
import { expandedContactSearch, } from '../../graphql/queries';
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle, destructiveButtonStyle } from '../../theme';



const types = {
	String: 0,
	ISODateTime: 1,
	Boolean: 2,
	Timezone: 3,
	State: 4,
	Epoch: 5,
	DateTime: 5, // The custom field DateTime is an epoch.
	Number: 6,
	Phone: 7
};

const modes = {
	Delete: 0,
	Update: 1
};

export function BulkEdit() {
	const navigate = useNavigate();
	const usStates = US[2];
	const client = generateClient();
	const userContext = useContext(UserContext);
	const [total, setTotal] = useState(0);
	const [retrievedCount, setRetrievedCount] = useState(0);
	const newSearchFilter = useRef({});
	const [contacts, setContacts] = useState([]);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [queryFields, setQueryFields] = useState([]);
	const [mode, setMode] = useState('Update');
	const [columns, setColumns] = useState([
		{ title: 'First Name', field: 'firstName' },
		{ title: 'Last Name', field: 'lastName' },
		{ title: 'Source', field: 'source' },
		{ title: 'Phone', field: 'phone' },
		{ title: 'Cell', field: 'cell' },
		{ title: 'Email', field: 'email' },
		{ title: 'State', field: 'state' },

	]);

	const progressCountRef = useRef(0);
	const [progressCount, setProgressCount] = useState(0);
	const errorCountRef = useRef(0);
	const [errorCount, setErrorCount] = useState(0);
	const [progressDialogOpen, setProgressDialogOpen] = useState(false);
	const [processDialogTitle, setProcessDialogTitle] = useState('');
	const queryPreviewThrottler = useRef(_.throttle((query) => logQuery(query), 1000, { 'leading': false, 'trailing': true }));
	const [customFieldColumns, setCustomFieldColumns] = useState([]);
	const [scheduleDeleteOpen, setScheduleDeleteOpen] = useState(false);
	const [deleteTime, setDeleteTime] = useState(moment().add(1, 'day').format('YYYY-MM-DDTHH:mm'));

	const csvChunks = useRef([]);

	const valueInputTypes = useMemo(() => ({
		firstName: types.String,
		lastName: types.String,
		phone: types.Phone,
		source: types.String,
		cell: types.Phone,
		email: types.String,
		address1: types.String,
		address2: types.String,
		city: types.String,
		state: types.State,
		zip: types.String,
		outboundCallerId: types.String,
		assignedAgent: types.String,
		ttl: types.Epoch,
		...userContext.customFields?.reduce((acc, cur) => Object.assign(acc, { [`custom:${cur.name}`]: types[cur.type] }), {})
	}), [userContext.customFields]);

	useEffect(() => {
		if (customFieldColumns.length > 0 && userContext) {
			const columns = [
				{ title: 'First Name', field: 'firstName' },
				{ title: 'Last Name', field: 'lastName' },
				{ title: 'Source', field: 'source' },
				{ title: 'Phone', field: 'phone' },
				{ title: 'Cell', field: 'cell' },
				{ title: 'Email', field: 'email' },
				{ title: 'State', field: 'state' },
			];
			for (const customField of customFieldColumns) {
				const title = userContext.customFields.find(field => field.name === customField).displayName;
				columns.push({ title, field: `customFields.${customField}` });
			}
			setColumns(columns);
		}
	}, [customFieldColumns, userContext.customFields]);


	useEffect(() => {
		if (userContext.queryFields.list) {
			setQueryFields([...userContext.queryFields.list])
		}
	}, [userContext.queryFields])

	function standardizeValue(values) {
		try {
			let value
			switch (valueInputTypes[values.fieldName]) {
				case types.String:
					return values.fieldValue;
				case types.Phone:
					return phone(values.fieldValue, { validateMobilePrefix: false }).phoneNumber || values.fieldValue;
				case types.ISODateTime:
					return new Date(values.fieldValue).toISOString();
				case types.Epoch:
					return new Date(values.fieldValue).getTime() / 1000;
				case types.Number:
					return Number(values.fieldValue)
				case types.Boolean:
					return !!values.fieldName;
				default:
					return null;
			}

		} catch (err) {
			console.error('Unable to get value', err)
		}

	}

	const logQuery = (query) => {
		if (query.rules) {
			if (query.rules.length > 0) {
				newSearchFilter.current = processFilterArray(query.rules, query.combinator);
				previewContacts();
			} else {
				//If we are missing any rules, clear the data
				setTotal(0);
			}
		}

		return;
	}

	const processFilterArray = (items, combinator) => {
		const ruleArray = [];
		if (userContext.tenantId) {
			const ruleGroup = { [combinator]: ruleArray };
			//the rule array always has to have the tenant included for security
			_.forEach(items, (item) => { ruleArray.push(processFilterItem(item, items.combinator)) });
			const tenantRuleGroup = { and: [{ tenant: { eq: userContext.tenantId } }, ruleGroup] };
			return tenantRuleGroup;
		} else {
			return {}
		}
	}

	const processFilterItem = (item) => {
		const cpyItem = { ...item };
		if (item.field) {
			//item.value = item.value.toLowerCase();
			if (!item.field.startsWith('custom:')) {
				if (cpyItem.operator === 'wildcard') {
					cpyItem.value = cpyItem.value.toLowerCase();
					cpyItem.value = `*${cpyItem.value}*`;
				}
				if (cpyItem.operator === 'wildcard*') {
					cpyItem.value = cpyItem.value.toLowerCase();
					cpyItem.value = `${cpyItem.value}*`;
					cpyItem.operator = 'wildcard';
				}
				if (cpyItem.operator === '*wildcard') {
					cpyItem.value = cpyItem.value.toLowerCase();
					cpyItem.value = `*${cpyItem.value}`;
					cpyItem.operator = 'wildcard';
				}
				const ruleItem = { [cpyItem.field]: { [cpyItem.operator]: cpyItem.value } };
				return ruleItem;
			} else {
				let fieldName = cpyItem.field.split(':')[1];
				if (cpyItem.operator === 'wildcard') {
					cpyItem.value = `*${cpyItem.value}*`;
					fieldName += '.keyword';
				} else if (cpyItem.operator === 'wildcard*') {
					cpyItem.value = `${cpyItem.value}*`;
					cpyItem.operator = 'wildcard';
					fieldName += '.keyword';
				} else if (cpyItem.operator === '*wildcard') {
					cpyItem.value = `*${cpyItem.value}`;
					cpyItem.operator = 'wildcard';
					fieldName += '.keyword';
				} else {
					if (cpyItem.value === true || cpyItem.value === false || cpyItem.value === 'true' || cpyItem === 'false') {
						if (_.isString(cpyItem.value)) {
							cpyItem.value = cpyItem.value === 'true';
						} else {
							cpyItem.value = cpyItem.value === true;
						}
					} else if (!isNaN(cpyItem.value)) {
						cpyItem.value = +cpyItem.value;
					} else if (isCustomFieldDate(fieldName) && !!Date.parse(cpyItem.value)) {
						cpyItem.value = Date.parse(cpyItem.value);
					}
				}
				const ruleItem = { [`customFields.${fieldName}`]: { [cpyItem.operator]: cpyItem.value } };
				return ruleItem;
			}
		}

		return processFilterArray(cpyItem.rules, cpyItem.combinator);
	}

	const isCustomFieldDate = (fieldName) => {
		const customField = _.find(userContext.customFields, ['name', fieldName]);
		if (customField) {
			return customField.type === 'Date';
		} else {
			return false;
		}
	}

	const previewContacts = async () => {
		try {
			let options = {
				limit: 250
			};
			options.filter = JSON.stringify(newSearchFilter.current);

			const listData = await client.graphql({
				query: expandedContactSearch,
				variables: options,
			});

			// We need to parse the custom fields out and NOT stringify them when we send them back out
			// because when these contacts are put back into DynamoDB, it is done through a DocumentClient.
			// The DocumentClient takes AWSJSON as is (not stringified).
			for (const contact of listData.data.expandedContactSearch.items) {
				if (contact.customFields != null && typeof (contact.customFields) === 'string') {
					try {
						contact.customFields = JSON.parse(contact.customFields);
					} catch (_) { }
				}
			}

			const contacts = listData.data.expandedContactSearch.items;

			setRetrievedCount(contacts.length ? contacts.length : 0);
			setTotal(listData.data.expandedContactSearch.total ? listData.data.expandedContactSearch.total : 0);

			setContacts([...contacts]);
		} catch (error) {
			console.error(error);
		}
	}

	function handleTypeChange(formikProps, e) {
		switch (valueInputTypes[e.target.value]) {
			default:
			case types.String:
			case types.ISODateTime:
			case types.DateTime:
			case types.Number:
			case types.Phone:
			case types.State:
			case types.Timezone:
				formikProps.setFieldValue('fieldValue', '');
				break;
			case types.Boolean:
				formikProps.setFieldValue('fieldValue', false);
				break;
		}

		formikProps.handleChange(e);
	}

	function customFieldUpdate(contact, field, value) {
		if (!('customFields' in contact)) {
			contact.customFields = {};
		}

		contact.customFields[field] = value;
	}

	function regularUpdate(contact, field, value) {
		contact[field] = value;
	}

	async function processContacts(values) {

		progressCountRef.current = 0;
		errorCountRef.current = 0;
		setProgressCount(0);
		setErrorCount(0);

		setConfirmOpen(false);
		// Retrieve each page of contacts and update them.
		if (mode === 'Update') {
			setProcessDialogTitle('Updating Contacts...');
		} else if (mode === 'Delete') {
			setProcessDialogTitle('Deleting Contacts...');
		} else if (mode === 'Scheduling') {
			setProcessDialogTitle('Scheduling Contacts...');
		} else {
			setProcessDialogTitle('Exporting Contacts...');
		}
		setProgressDialogOpen(true);

		// Fetch auth info once before processing to avoid repeated token refreshes
		let authToken;
		let updatedByName;
		try {
			const session = await fetchAuthSession();
			authToken = session.tokens.idToken;
			const attributes = await fetchUserAttributes();
			updatedByName = attributes.name;
		} catch (error) {
			console.error('Error fetching auth for bulk operation', error);
			enqueueSnackbar('Authentication error. Please refresh and try again.', { variant: 'error' });
			setProgressDialogOpen(false);
			return;
		}

		let options = {
			limit: 500
		};
		let nextToken = null;

		options.filter = JSON.stringify(newSearchFilter.current);
		do {
			if (nextToken) {
				options.nextToken = nextToken;
			}

			try {

				let listData = await client.graphql({
					query: expandedContactSearch,
					variables: options,
				});

				// We need to parse the custom fields out and NOT stringify them when we send them back out
				// because when these contacts are put back into DynamoDB, it is done through a DocumentClient.
				// The DocumentClient takes AWSJSON as is (not stringified).
				for (const contact of listData.data.expandedContactSearch.items) {
					if (contact.customFields != null && typeof (contact.customFields) === 'string') {
						try {
							contact.customFields = JSON.parse(contact.customFields);
						} catch (_) { }
					}
				}

				const retrievedContacts = listData.data.expandedContactSearch.items;

				nextToken = listData.data.expandedContactSearch.nextToken;

				const chunkedContacts = _.chunk(retrievedContacts, 100);

				if (mode !== 'Export') {

					const promises = [];
					for (const chunk of chunkedContacts) {
						promises.push(processBulkEdit(values, chunk, authToken, updatedByName));
					}

					await Promise.all(promises);

				} else {
					// Flatten the contacts into simple objects for CSV export
					const flatContacts = [];
					for (const contact of retrievedContacts) {
						const flatContact = { ...contact };
						if (flatContact.customFields) {
							for (const key in flatContact.customFields) {
								flatContact[`custom:${key}`] = flatContact.customFields[key];
							}
						}
						delete flatContact.customFields;
						flatContacts.push(flatContact);
					}
					csvChunks.current.push(...flatContacts);
				}

				progressCountRef.current += retrievedContacts.length;
				setProgressCount(progressCountRef.current);

			} catch (error) {
				const retrievedContacts = error.data.expandedContactSearch.items;
				const cleanedContacts = retrievedContacts.filter(contact => contact?.id);

				nextToken = error.data.expandedContactSearch.nextToken;

				const chunkedContacts = _.chunk(cleanedContacts, 100);

				if (mode !== 'Export') {
					const promises = [];
					for (const chunk of chunkedContacts) {
						promises.push(processBulkEdit(values, chunk, authToken, updatedByName));
					}

					await Promise.all(promises);
				} else {
					// Flatten the contacts into simple objects for CSV export
					const flatContacts = [];
					for (const contact of cleanedContacts) {
						const flatContact = { ...contact };
						if (flatContact.customFields) {
							for (const key in flatContact.customFields) {
								flatContact[`custom:${key}`] = flatContact.customFields[key];
							}
							delete flatContact.customFields;
							flatContacts.push(flatContact);
						}
						csvChunks.current.push(...flatContacts);
					}
				}

				errorCountRef.current += retrievedContacts.length - cleanedContacts.length;

				progressCountRef.current += retrievedContacts.length;
				setProgressCount(progressCountRef.current);
				setErrorCount(errorCountRef.current);
			}

		} while (nextToken);

		if (mode === 'Export') {
			await exportContacts();
		}

		setProgressDialogOpen(false);
		enqueueSnackbar(`Successful Contacts: ${progressCountRef.current} | Error Contacts: ${errorCountRef.current}`, {
			variant: 'success',
			autoHideDuration: 5000
		});

		setContacts([]);
		progressCountRef.current = 0;
		setTotal(0);
		setRetrievedCount(0);
	}

	async function processBulkEdit(values, contacts, authToken, updatedByName) {
		if (mode === 'Update' || mode === 'Scheduling') {
			const value = standardizeValue(values);
			try {
				const response = await post({
					apiName: 'cdyxoutreach',
					path: '/bulk-edit/edit',
					options: {
						headers: {
							Authorization: `Bearer ${authToken}`,
							'x-api-key': userContext.apiKey
						},
						body: {
							contacts,
							updateField: values.fieldName,
							updateValue: value,
							updatedBy: updatedByName
						}
					}
				}).response;

			} catch (err) {
				console.error('Unable to update contacts', err);
				enqueueSnackbar('Unable to update contacts', {
					variant: 'error',
					autoHideDuration: 5000
				});
			}
		} else {
			try {
				const response = await post({
					apiName: 'cdyxoutreach',
					path: '/bulk-edit/delete',
					options: {
						headers: {
							Authorization: `Bearer ${authToken}`,
							'x-api-key': userContext.apiKey
						},
						body: {
							contacts,
							deletedBy: updatedByName
						}
					}
				}).response;
			} catch (err) {
				console.error('Unable to delete contacts', err);
				enqueueSnackbar('Unable to delete contacts', {
					variant: 'error',
					autoHideDuration: 5000
				});
			}
		}

	}

	const handleCancelUpdate = () => {
		setConfirmOpen(false);
	}

	async function exportContacts() {
		const csvRows = [];
		const allChunks = csvChunks.current.flat(); // flatten all pages into one array

		if (!allChunks.length) {
			console.warn("No contacts to export.");
			return;
		}

		// build headers from all unique keys
		const headers = Array.from(
			allChunks.reduce((set, obj) => {
				Object.keys(obj).forEach(k => set.add(k));
				return set;
			}, new Set())
		);

		csvRows.push(headers.join(","));

		for (const contact of allChunks) {
			const row = headers
				.map(h => {
					let val = contact[h];
					if (val === undefined || val === null) return "";
					if (typeof val === "object") val = JSON.stringify(val);
					// Escape double quotes
					return `"${String(val).replace(/"/g, '""')}"`;
				})
				.join(",");
			csvRows.push(row);
		}

		const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "contacts.csv";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}


	return (
		<Box display='flex' flexDirection='column'>
			<Formik
				initialValues={{
					fieldName: '',
					fieldValue: '',
					query: {
						rules: [],
						combinator: 'and'
					}
				}}
				enableReinitialize={true}
				validationSchema={lazy(value => {
					let subSchema = mixed();

					try {
						subSchema = typeof (value.fieldName) === 'string' ? reach(contactValidation(userContext.customFields), value.fieldName.startsWith('custom:') ? `customFields.${value.fieldName.substring(7)}` : value.fieldName) : mixed();
					} catch (_) { }

					if (mode === 'Delete' || mode === 'Export') {
						return object({
							query: object({
								rules: array().min(1, 'You must have at least one rule in your query')
							})
						})
					}

					return object({
						fieldName: string().required('Please specify a field name'),
						fieldValue: subSchema,
						query: object({
							rules: array().min(1, 'You must have at least one rule in your query')
						})
					})
				})}
				onSubmit={values => {
					setConfirmOpen(true);
				}}
			>
				{formikProps => (
					<form onSubmit={formikProps.handleSubmit}>
						<Box display='flex' flexDirection='column' gap={2}>
							<PageAppBar
								title='Bulk Edit & Delete'
							// description="Bulk edit or delete contacts based on a query."
							/>

							<Card style={cardStyle} variant='outlined' >
								<CardHeader title="Query Builder" slotProps={{
									title: {
										variant: 'h6'
									}
								}}
									subheader={formikProps.errors?.query?.rules && formikProps.touched?.query?.rules ? formikProps.errors.query.rules : ''}
								/>
								<CardContent>
									{userContext.queryFields?.list && <QueryBuilder
										name='query'
										query={formikProps.values.query}
										fields={userContext.queryFields.list}
										onQueryChange={e => {
											formikProps.setFieldValue('query', e);
											queryPreviewThrottler.current(e);
											const customFields = [];
											for (const field of e.rules) {
												if (field?.field?.startsWith('custom:')) {
													const customField = field.field.split(':')[1];
													if (!customFieldColumns.includes(customField)) {
														customFields.push(customField);
													}
												}
											}
											setCustomFieldColumns(customFields);
										}}
										showCombinatorsBetweenRules
										getValueEditorType={field => userContext.queryFields.typeLookup(field)}
									/>}
								</CardContent>
							</Card>
							<Box display="grid" gridTemplateColumns="3fr 2fr 2fr" gap={2}>

								<Card
									style={cardStyle}
									variant="outlined">
									<CardHeader title='Bulk Edit' slotProps={{
										title: {
											variant: 'h6'
										}
									}} />
									<CardContent>
										<Box display='flex' alignItems='center' gap={2} >
											<Typography variant="body" >Update</Typography>
											<Box>
												<FormControl>
													<InputLabel error={formikProps.errors.fieldName && formikProps.touched.fieldName}>Field Name</InputLabel>
													<Select
														labelId='field-name'
														label='Field Name'
														sx={{ minWidth: '200px' }}
														error={formikProps.errors.fieldName && formikProps.touched.fieldName}
														name="fieldName"
														value={formikProps.values.fieldName}
														onChange={e => handleTypeChange(formikProps, e)}
														onBlur={formikProps.handleBlur}>
														{queryFields.map((field, index) =>
															<MenuItem key={index} value={field.name}>{field.label}</MenuItem>)}
													</Select>
													{formikProps.errors.fieldName && formikProps.touched.fieldName &&
														<FormHelperText error>{formikProps.errors.fieldName}</FormHelperText>}
												</FormControl>
											</Box>
											<Typography variant='body' >to</Typography>
											<Box>
												{function () {
													switch (valueInputTypes[formikProps.values.fieldName]) {
														case types.String:
														case types.ISODateTime:
														case types.DateTime:
														case types.Number:
														case types.Phone:
															return (
																<TextField
																	label='Field Value'
																	name='fieldValue'
																	type={valueInputTypes[formikProps.values.fieldName] === types.String || valueInputTypes[formikProps.values.fieldName] === types.Phone
																		? 'text'
																		: valueInputTypes[formikProps.values.fieldName] === types.ISODateTime || valueInputTypes[formikProps.values.fieldName] === types.DateTime
																			? 'datetime-local'
																			: 'number'}
																	value={formikProps.values.fieldValue}
																	onChange={formikProps.handleChange}
																	onBlur={formikProps.handleBlur}
																	error={formikProps.errors?.fieldValue && formikProps.touched?.fieldValue}
																	helperText={formikProps.touched?.fieldValue && formikProps.errors?.fieldValue}
																/>
															);
														case types.State:
															return (
																<FormControl>
																	<InputLabel error={formikProps.errors?.fieldValue && formikProps.touched?.fieldValue}>Field Value</InputLabel>
																	<Select sx={{ minWidth: '200px' }} name='fieldValue' label="Field Value" onChange={formikProps.handleChange} onBlur={formikProps.handleBlur} value={formikProps.values.fieldValue}>
																		{usStates.map(state =>
																			<MenuItem key={state[1]} value={state[1]}>{state[0]}</MenuItem>)}
																	</Select>
																	{formikProps.errors?.fieldValue && formikProps.touched?.fieldValue &&
																		<FormHelperText error>{formikProps.errors.fieldValue}</FormHelperText>}
																</FormControl>
															);
														case types.Boolean:
															return (
																<FormControlLabel label='Field Value' control={
																	<Switch name='fieldValue' checked={formikProps.values.fieldValue} onChange={formikProps.handleChange} />
																} />
															);
														default:
															return null;
													}
												}()}
											</Box>
											{formikProps.values.fieldName && <Button sx={actionOneButtonStyle} type='submit' onClick={() => setMode('Update')}>Update {total} Contacts</Button>}
										</Box>
									</CardContent>
								</Card>
								<Card
									style={cardStyle}
									variant="outlined"
								>
									<CardHeader title='Bulk Delete' slotProps={{
										title: {
											variant: 'h6'
										}
									}} />
									<CardContent>
										<Box display='flex' alignItems='center' gap={2} >
											<Button sx={destructiveButtonStyle} type='submit' onClick={() => setMode('Delete')}>{`Delete ${total} Contacts`}</Button>
											<Button sx={actionTwoButtonStyle} startIcon={<ScheduleOutlined />} onClick={() => setScheduleDeleteOpen(true)}>Schedule Delete</Button>
										</Box>
									</CardContent>
								</Card>
								<Card
									style={cardStyle}
									variant="outlined"
								>
									<CardHeader title='Bulk Export' slotProps={{
										title: {
											variant: 'h6'
										}
									}} />
									<CardContent>
										<Button sx={actionOneButtonStyle} type='submit' onClick={() => setMode('Export')}>{`Export ${total} Contacts`}</Button>
									</CardContent>
								</Card>
							</Box>

							<Card variant='outlined' style={cardStyle}>
								<CardContent>
									<MaterialTable
										components={{
											// Toolbar: toolBar,
											Container: (props) => <Box {...props} elevation={0} />,
										}}
										title={`Preview ${retrievedCount} of ${total} matched contacts.`}
										columns={columns}
										data={contacts}
										options={{
											actionsColumnIndex: -1,
											pageSize: 5,
											searchFieldStyle: {
												marginBottom: '16px',
												marginleft: '-28px',
											},
											headerStyle: {
												borderTop: '1px solid #e0e0e0',
												padding: '16px',
											},
											searchFieldVariant: 'outlined'
										}}
										onRowClick={(event, rowData) => {
											navigate(`/contacts/${rowData.id}`);
										}}
									/>
								</CardContent>
							</Card>

						</Box>

						<ConfirmDialog
							open={confirmOpen}
							title={`${mode === 'Update' ? 'Update' : mode === 'Delete' ? 'Delete' : 'Export'} ${total} Contacts`}
							description={`Are you sure you want to ${mode === 'Update' ? 'update' : mode === 'Delete' ? 'delete' : 'export'} these contacts?`}
							actionOneText={mode}
							actionOneHandler={() => processContacts(formikProps.values)}
							actionTwoText="Cancel"
							actionTwoHandler={handleCancelUpdate}
						/>
						<Dialog open={progressDialogOpen}>
							<DialogTitle>{processDialogTitle}</DialogTitle>
							<DialogContent>
								<Typography variant='subtitle2' >Processed {progressCount} of {total} contacts...</Typography>
								<LinearProgress variant='determinate' value={progressCount / total * 100} />
							</DialogContent>
						</Dialog>

						<Dialog open={scheduleDeleteOpen}>
							<DialogTitle>Schedule Delete</DialogTitle>
							<DialogContent>
								<TextField
									label='Scheduled Delete Time'
									type='datetime-local'
									sx={{ minWidth: '250px', marginTop: '16px' }}
									value={deleteTime}
									onChange={e => setDeleteTime(e.target.value)}
									InputLabelProps={{
										shrink: true,
									}}
								/>
							</DialogContent>
							<DialogActions>
								<Box display="flex" gap={2}>
									<Button sx={actionTwoButtonStyle} onClick={() => setScheduleDeleteOpen(false)}>Cancel</Button>
									<Button sx={destructiveButtonStyle} onClick={() => {
										// Set to an update and update the ttl on the contact to the time in epoch
										setMode('Scheduling');
										processContacts({ ...formikProps.values, fieldName: 'ttl', fieldValue: deleteTime });
										setScheduleDeleteOpen(false);
									}}>Schedule Delete for {total} Contacts</Button>
								</Box>

							</DialogActions>
						</Dialog>
					</form>
				)}
			</Formik>
		</Box>
	)
}
